// R115 maint — booking a work order onto a day from anywhere on the calendar
// (the WO inspector's Book / Move, a 📥 bucket row, a dragged chip). One path:
//   pick a day (ctx.pickDay — touch-safe, refuses past days, ALLOWS taken
//   nights: WO work during a stay is legitimate) → if a guest is staying that
//   night and the WO has no guest permission yet, an inline confirm in the form
//   sheet (with the permission toggle) → POST /work-orders/:id/schedule
//   {date, by, note} → toast with Undo → reload the base payload, the layer and
//   the inspector. Never window.confirm, never z 9000.
import type { CalendarCtx } from '../registry';
import { api, WO_API, LAYER_ID, nightGuest, nightWarn, wmd, md } from './shared';

export interface BookTarget { id: string; listing_id: string; title: string; prev: string | null; guest_ok: boolean; prev_window?: { start: string; end: string | null } | null }

// (core already vetoes the auto-refresh while its pickDay strip / a sheet is open; this
// flag only covers the window between "Book anyway" and the reload landing)
let sheetBusy = false;
export const bookingBusy = (): boolean => sheetBusy;

async function refreshAll(ctx: CalendarCtx): Promise<void> {
  try { await ctx.reload({ base: true, layers: [LAYER_ID], inspector: true }); } catch { /* the page reports its own load errors */ }
}

/** POST the booking (or unbooking: date null) and offer Undo. Resolves true when it landed. */
export async function bookWo(ctx: CalendarCtx, t: BookTarget, date: string | null, extra: { gp?: { granted: boolean; mode: string } | null; note?: string; silent?: boolean } = {}): Promise<boolean> {
  const r = await api('POST', `${WO_API}/${encodeURIComponent(t.id)}/schedule`, { date, by: ctx.actorName(), note: extra.note || (date ? 'booked from the calendar' : 'unbooked from the calendar'), source: 'calendar' });
  if (!r.ok) { ctx.toast(`Couldn't ${date ? 'book' : 'unbook'} “${t.title}” — ${r.error}`); return false; }
  let gpSet = false;
  if (extra.gp && extra.gp.granted) {
    const g = await api('POST', `${WO_API}/${encodeURIComponent(t.id)}`, { guest_permission: { granted: true, mode: extra.gp.mode === 'own_code' ? 'own_code' : 'guest_present' }, by: ctx.actorName(), surface: 'calendar' });
    if (!g.ok) ctx.toast(`Booked, but the guest permission didn't save — ${g.error}`);
    else gpSet = !t.guest_ok;   // this booking granted it
  }
  if (!extra.silent) {
    const prev = t.prev;
    // R115 review: Undo puts back what THIS action changed — the whole previous window (a multi-day PM vacancy
    // window, not just its first day) and a guest permission the booking itself granted
    const prevWin = prev && t.prev_window && String(t.prev_window.start || '').slice(0, 10) === prev ? { start: prev, end: t.prev_window.end ? String(t.prev_window.end).slice(0, 10) : null } : null;
    const msg = date ? `Booked “${t.title}” for ${wmd(date)}` : `Unbooked “${t.title}”`;
    ctx.toastUndo(msg, () => { void (async () => {
      const u = await api('POST', `${WO_API}/${encodeURIComponent(t.id)}/schedule`, { date: prev, by: ctx.actorName(), note: 'undo from the calendar', source: 'calendar', ...(prevWin ? { restore_window: prevWin } : {}) });
      if (u.ok && gpSet) {
        const g = await api('POST', `${WO_API}/${encodeURIComponent(t.id)}`, { guest_permission: null, by: ctx.actorName(), surface: 'calendar', note: 'guest permission cleared (undo from the calendar)' });
        if (!g.ok) ctx.toast(`Undone, but the guest permission is still on — ${g.error}`);
      }
      ctx.toast(u.ok ? (prev ? `Back on ${wmd(prev)}` : 'Back to unscheduled') : `Undo failed — ${u.error}`);
      await refreshAll(ctx);
    })(); });
  }
  await refreshAll(ctx);
  return true;
}

/** The inline "a guest is staying that night" confirm (form sheet), or a straight booking. */
export function guardGuestNight(ctx: CalendarCtx, t: BookTarget, date: string): void {
  if (date < ctx.asOf) { ctx.toast("That day has passed — pick today or later."); return; }
  const g = nightGuest(ctx, t.listing_id, date);
  if (!g || t.guest_ok) { void bookWo(ctx, t, date); return; }
  const p = ctx.property(t.listing_id);
  const home = (p && (p.name || p.full_name)) || 'this home';
  const sheet = ctx.openSheet(`
    <div class="mw mw-sheet" data-mw-guestnight>
      <p class="mw-warn"><b>${ctx.esc(home)}</b> · <b>${ctx.esc(wmd(date))}</b> — ${nightWarn(g, wmd(date), ctx.esc)}</p>
      <p class="mw-muted">Book “${ctx.esc(t.title)}” that day anyway?</p>
      <label class="mw-check"><input type="checkbox" data-mw-gp> 🚪 ${g.kind === 'owner' ? 'Owner' : 'Guest'} gave permission to enter</label>
      <label class="mw-field">Access mode
        <select data-mw-gpmode disabled><option value="guest_present">${g.kind === 'owner' ? 'owner' : 'guest'} will be present</option><option value="own_code">enter with own code</option></select>
      </label>
      <div class="mw-err" data-mw-err hidden></div>
      <div class="mw-sheet-foot" data-bottom-bar>
        <button type="button" class="btn btn-secondary" data-mw-cancel>Cancel</button>
        <button type="button" class="btn btn-primary" data-mw-go>Book ${ctx.esc(md(date))} anyway</button>
      </div>
    </div>`, { title: g.kind === 'unknown' ? 'Book on a day the calendar hasn\'t loaded' : 'Book on an occupied night', onClose: () => { sheetBusy = false; ctx.markDirty(false); } });
  sheetBusy = true;
  const gp = sheet.querySelector<HTMLInputElement>('[data-mw-gp]');
  const mode = sheet.querySelector<HTMLSelectElement>('[data-mw-gpmode]');
  gp?.addEventListener('change', () => { if (mode) mode.disabled = !gp.checked; });
  sheet.querySelector('[data-mw-cancel]')?.addEventListener('click', () => { sheetBusy = false; ctx.closeSheet(); });
  const go = sheet.querySelector<HTMLButtonElement>('[data-mw-go]');
  go?.addEventListener('click', async () => {
    if (!go) return;
    go.disabled = true; go.textContent = 'Booking…';
    const ok = await bookWo(ctx, t, date, { gp: gp && gp.checked ? { granted: true, mode: mode ? mode.value : 'guest_present' } : null, note: `booked from the calendar on an occupied night${gp && gp.checked ? ' (guest permission given)' : ''}` });
    sheetBusy = false;
    if (ok) ctx.closeSheet();
    else { go.disabled = false; go.textContent = `Book ${md(date)} anyway`; }
  });
}

/** Book / Move via the touch-safe 21-day strip. */
export function pickAndBook(ctx: CalendarCtx, t: BookTarget): void {
  ctx.pickDay({
    listing_id: t.listing_id,
    label: t.prev ? `Move “${t.title}” (now ${md(t.prev)})` : `Book “${t.title}”`,
    allowPast: false,
    allowTaken: true,
    onPick: (date: string) => { if (t.prev && date === t.prev) { ctx.toast(`Already booked for ${wmd(date)}.`); return; } guardGuestNight(ctx, t, date); },
  });
}
